import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getCurrentUserId } from '../utils/userStorage';

const StatusTab = ({ allParticipations = [], selectedCell, onCellSelect, selectedChallengeId, challenges = [] }) => {
    const { t } = useTranslation();
    const authUser = useSelector(state => state.auth?.user);
    const { participantsList } = useMemo(() => {
        // 현재 선택된 참여그룹 정보 가져오기
        let selectedGroupId = null;
        const savedGroup = localStorage.getItem('selectedParticipationGroup');
        if (savedGroup) {
            try {
                const parsedGroup = JSON.parse(savedGroup);
                selectedGroupId = parsedGroup.qryChallengePtcpGrpId;
            } catch (error) {
                console.error('참여그룹 정보 파싱 오류:', error);
            }
        }

        console.log('🔍 [StatusTab] 필터링 전 데이터:', allParticipations.length, '건');
        console.log('🔍 [StatusTab] 선택된 참여그룹ID:', selectedGroupId);

        // 참여그룹으로 필터링
        const filteredParticipations = allParticipations.filter(item => {
            // 선택된 참여그룹이 있으면 해당 그룹 데이터만
            if (selectedGroupId) {
                return item.QRY_CHALLENGE_PTCP_GRP_ID === selectedGroupId;
            }
            // 선택된 참여그룹이 없으면 그룹ID가 null인 데이터만
            return !item.QRY_CHALLENGE_PTCP_GRP_ID;
        });

        console.log('✅ [StatusTab] 필터링 후 데이터:', filteredParticipations.length, '건');
        if (filteredParticipations.length > 0) {
            console.log('🔍 [StatusTab] 필터링된 데이터 샘플 (첫 3개):');
            filteredParticipations.slice(0, 3).forEach((item, index) => {
                console.log(`  [${index}] 그룹ID:`, item.QRY_CHALLENGE_PTCP_GRP_ID, '| 사용자:', item.USRID, '| 퀴즈:', item.QUIZ_NO);
            });
        }

        const participants = filteredParticipations.reduce((acc, item) => {
            const userId = item.USRID;
            const userName = item.USER_KNME || userId;
            if (!acc[userId]) {
                acc[userId] = {
                    name: userName,
                    usrid: userId,
                    lastSubmissionTime: null,
                    submissions: []
                };
            }
            acc[userId][`q${item.QUIZ_NO}`] = item.SUCCSS_YN === 'Y' ? 'success' : 'fail';   // 언어 중립 토큰 — 비교/색상 분기에만 쓴다

            // 제출 시간 정보 수집 (YYYYMMDDHHMMSS 형식 파싱)
            if (item.SBMSN_DT) {
                const dateStr = String(item.SBMSN_DT);
                if (dateStr.length === 14) {
                    // YYYYMMDDHHMMSS 형식을 Date 객체로 변환
                    const year = parseInt(dateStr.substr(0, 4));
                    const month = parseInt(dateStr.substr(4, 2)) - 1; // month는 0-based
                    const day = parseInt(dateStr.substr(6, 2));
                    const hour = parseInt(dateStr.substr(8, 2));
                    const minute = parseInt(dateStr.substr(10, 2));
                    const second = parseInt(dateStr.substr(12, 2));

                    acc[userId].submissions.push(new Date(year, month, day, hour, minute, second));
                }
            }

            return acc;
        }, {});

        // 각 참여자의 최종 제출일시 계산 및 정렬
        const participantsList = Object.values(participants).map(participant => {
            if (participant.submissions.length > 0) {
                // 가장 최근 제출일시 찾기
                participant.lastSubmissionTime = new Date(Math.max(...participant.submissions));
            }
            // submissions 배열은 정리 (메모리 절약)
            delete participant.submissions;
            return participant;
        });

        // 최종 제출일시 역순으로 정렬 (최근 제출자가 먼저)
        participantsList.sort((a, b) => {
            if (!a.lastSubmissionTime && !b.lastSubmissionTime) return 0;
            if (!a.lastSubmissionTime) return 1; // 제출하지 않은 사용자는 뒤로
            if (!b.lastSubmissionTime) return -1; // 제출한 사용자는 앞으로
            return b.lastSubmissionTime - a.lastSubmissionTime; // 최근 제출자가 먼저
        });

        return {
            participantsList
        };
    }, [allParticipations]);

    // 제출 쿼리 조회 권한 체크 함수 (ParticipationTab과 동일)
    const canViewSubmissionQuery = (targetUsrid) => {
        // 현재 사용자 ID 가져오기
        const currentUserId = authUser?.usrid || authUser?.id || authUser?.username || getCurrentUserId();

        console.log('권한 체크:', {
            currentUserId,
            targetUsrid,
            selectedChallengeId
        });

        // 1. 본인이 제출한 경우
        if (String(targetUsrid) === String(currentUserId)) {
            console.log('권한 허용: 본인 제출');
            return true;
        }

        // 2. 담당사용자인 경우 (선택된 챌린지의 MGR_USRID와 비교)
        const selectedChallenge = challenges.find(ch => ch.QRY_CHALLENGE_ID === selectedChallengeId);
        console.log('🔍 찾은 챌린지 전체:', JSON.stringify(selectedChallenge, null, 2));
        console.log('🔍 챌린지 객체의 모든 키:', selectedChallenge ? Object.keys(selectedChallenge) : []);
        console.log('🔍 챌린지 담당자 ID (MGR_USRID):', selectedChallenge?.MGR_USRID);

        if (selectedChallenge && selectedChallenge.MGR_USRID &&
            String(selectedChallenge.MGR_USRID) === String(currentUserId)) {
            console.log('권한 허용: 챌린지 담당자');
            return true;
        }

        console.log('권한 거부: 조건 불일치');
        return false;
    };

    const getSubmissionQuery = (usrid, quizNo) => {
        // 권한 체크 먼저 수행
        if (!canViewSubmissionQuery(usrid)) {
            return t('challenge:status.noPermission');
        }

        console.log('getSubmissionQuery 호출:', { usrid, quizNo, selectedChallengeId });

        // 현재 선택된 참여그룹 정보 가져오기
        let selectedGroupId = null;
        const savedGroup = localStorage.getItem('selectedParticipationGroup');
        if (savedGroup) {
            try {
                const parsedGroup = JSON.parse(savedGroup);
                selectedGroupId = parsedGroup.qryChallengePtcpGrpId;
            } catch (error) {
                console.error('참여그룹 정보 파싱 오류:', error);
            }
        }

        const submission = allParticipations.find(item => {
            const matchChallengeId = !selectedChallengeId || item.QRY_CHALLENGE_ID === selectedChallengeId;
            const matchUserId = item.USRID === usrid;
            const matchQuizNo = String(item.QUIZ_NO) === String(parseInt(quizNo.replace('q', '')));

            // 참여그룹 필터링
            let matchGroup;
            if (selectedGroupId) {
                matchGroup = item.QRY_CHALLENGE_PTCP_GRP_ID === selectedGroupId;
            } else {
                matchGroup = !item.QRY_CHALLENGE_PTCP_GRP_ID;
            }

            return matchChallengeId && matchUserId && matchQuizNo && matchGroup;
        });

        console.log('찾은 제출 기록:', submission);
        return submission?.SBMSN_SQL_CN || t('challenge:status.noQueries');
    };

    // 날짜 포맷팅 함수
    const formatDateTime = (dateTime) => {
        if (!dateTime) return '-';

        const date = new Date(dateTime);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}`;
    };

    const getCellStyle = (usrid, quizKey) => {
        const isSelected = selectedCell?.usrid === usrid && selectedCell?.quizKey === quizKey;
        const participant = participantsList.find(p => p.usrid === usrid);
        const status = participant?.[quizKey];

        let backgroundColor = 'var(--color-bg)';
        let color = 'var(--color-text)';

        if (status === 'success') {
            backgroundColor = 'var(--color-success-bg)';
            color = 'var(--color-success-text)';
        } else if (status === 'fail') {
            backgroundColor = 'var(--color-error-bg)';
            color = 'var(--color-error-text)';
        }

        if (isSelected) {
            backgroundColor = 'var(--color-accent)';
            color = 'var(--color-bg)';
        }

        return {
            padding: '4px 8px',
            textAlign: 'center',
            border: '1px solid var(--color-border)',
            cursor: status ? 'pointer' : 'default',
            backgroundColor,
            color,
            fontSize: '12px'
        };
    };

    // 현재 선택된 참여그룹명 가져오기
    const getSelectedGroupName = () => {
        const savedGroup = localStorage.getItem('selectedParticipationGroup');
        if (savedGroup) {
            try {
                const parsedGroup = JSON.parse(savedGroup);
                return parsedGroup.ptcpGrpNm;
            } catch (error) {
                return null;
            }
        }
        return null;
    };

    const selectedGroupName = getSelectedGroupName();

    return (
        <div style={{ padding: '0 16px' }}>
            {/* 참여자별 제출현황 테이블 */}
            <div style={{ marginBottom: 20 }}>
                <h4 style={{ marginBottom: 10, color: 'var(--color-text)', fontSize: '16px' }}>
                    {t('challenge:status.title')}
                    {selectedGroupName ? (
                        <span style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginLeft: '8px' }}>
                            ({selectedGroupName})
                        </span>
                    ) : (
                        <span style={{ fontSize: '14px', color: 'var(--color-text-muted)', marginLeft: '8px' }}>
                            {t('challenge:status.noGroup')}
                        </span>
                    )}
                </h4>
                <div style={{ border: '1px solid var(--color-border)', maxHeight: '300px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                            <tr style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                                <th style={{ padding: '6px', border: '1px solid var(--color-border)', width: '100px', maxWidth: '100px' }}>
                                    {t('challenge:status.participant')}
                                </th>
                                <th style={{ padding: '6px', border: '1px solid var(--color-border)', width: '120px', maxWidth: '120px' }}>
                                    {t('challenge:status.lastSubmitted')}
                                </th>
                                {Array.from({ length: 10 }, (_, i) => (
                                    <th key={`q${i + 1}`} style={{ padding: '4px', border: '1px solid var(--color-border)', width: '40px', maxWidth: '40px' }}>
                                        Q{i + 1}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {participantsList.map((participant) => (
                                <tr key={participant.usrid}>
                                    <td style={{
                                        padding: '6px 4px',
                                        border: '1px solid var(--color-border)',
                                        fontWeight: 'bold',
                                        fontSize: '11px',
                                        width: '100px',
                                        maxWidth: '100px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {participant.name}
                                    </td>
                                    <td style={{
                                        padding: '6px 4px',
                                        border: '1px solid var(--color-border)',
                                        fontSize: '10px',
                                        color: participant.lastSubmissionTime ? 'var(--color-text-secondary)' : 'var(--color-text-muted)',
                                        width: '120px',
                                        maxWidth: '120px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {formatDateTime(participant.lastSubmissionTime)}
                                    </td>
                                    {Array.from({ length: 10 }, (_, i) => {
                                        const quizKey = `q${i + 1}`;
                                        const status = participant[quizKey];
                                        return (
                                            <td
                                                key={quizKey}
                                                style={getCellStyle(participant.usrid, quizKey)}
                                                onClick={() => {
                                                    if (status) {
                                                        onCellSelect({
                                                            usrid: participant.usrid,
                                                            quizKey: quizKey,
                                                            userName: participant.name,
                                                            quizNo: i + 1,
                                                            status: status
                                                        });
                                                    }
                                                }}
                                            >
                                                {status || '-'}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 선택된 셀의 제출 쿼리 표시 */}
            {selectedCell && (
                <div style={{
                    padding: 15,
                    backgroundColor: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 5
                }}>
                    <h5 style={{ marginTop: 0, marginBottom: 10, color: 'var(--color-text)' }}>
                        {t('challenge:status.answerHeader', {
                            name: selectedCell.userName,
                            quizNo: selectedCell.quizNo,
                            result: t(`challenge:result.${selectedCell.status}`),
                        })}
                    </h5>
                    <pre style={{
                        backgroundColor: 'var(--color-bg)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 4,
                        padding: 10,
                        whiteSpace: 'pre-wrap',
                        fontSize: 13,
                        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                        maxHeight: 300,
                        overflow: 'auto',
                        margin: 0
                    }}>
                        {getSubmissionQuery(selectedCell.usrid, selectedCell.quizKey) || t('challenge:status.noAnswers')}
                    </pre>
                </div>
            )}
        </div>
    );
};

export default StatusTab;